import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { addBusiness } from '../../api/business';
import useFetchBusinesses from '../../hooks/useFetchBusinesses';

import NewBusinessIcon from '../../assets/icons/NewBusinessModal.svg';
import CloseModalIcon from '../../assets/icons/CloseModal.svg';
import UploadIcon from '../../assets/icons/Upload-img.svg';
import InfoIcon from '../../assets/icons/info.svg';

import './CreateBusinessModal.scss';

const CreateBusinessModal = ({ isOpen, onClose }) => {
  const [isSending, setIsSending] = useState(false);
  const [images, setImages] = useState('');
  const [prevImage, setPrevImage] = useState(null);
  const { t } = useTranslation('business');
  const navigate = useNavigate();

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
  });

  const categoryTexts = [
    t('create_business.categories.automotive'),
    t('create_business.categories.beauty_spa_salon'),
    t('create_business.categories.clothing_apparel'),
    t('create_business.categories.education'),
    t('create_business.categories.entertainment'),
    t('create_business.categories.online_gambling_gaming'),
    t('create_business.categories.non_online_gambling_gaming'),
    t('create_business.categories.event_planning_service'),
    t('create_business.categories.finance_banking'),
    t('create_business.categories.food_grocery'),
    t('create_business.categories.alcoholic_beverages'),
    t('create_business.categories.public_service'),
    t('create_business.categories.hotel_lodging'),
    t('create_business.categories.medical_health'),
    t('create_business.categories.over_counter_drugs'),
    t('create_business.categories.non_profit'),
    t('create_business.categories.professional_services'),
  ];

  // Function to convert an image file into base64 string (handleImageUpload)
  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageChange = (file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPrevImage(reader.result); // Set the image data URL
      };
      reader.readAsDataURL(file); // Read the file as a data URL
    }
  };

  //:: Function to handle image loading
  const handleImageUpload = (event) => {
    const file = event.target.files[0];

    handleImageChange(file);

    if (
      file &&
      (file.type === 'image/jpeg' ||
        file.type === 'image/png' ||
        file.type === 'image/jpg')
    ) {
      convertImageToBase64(file)
        .then((base64String) => {
          const cleanedBase64 = base64String.replace(
            /^data:image\/[a-z]+;base64,/,
            ''
          );
          setImages(() => {
            setValue('logo', cleanedBase64, { shouldValidate: true });
            return cleanedBase64;
          });
        })
        .catch((error) => {
          console.error('Error converting the image to base64:', error);
        });
    }
  };

  const resetImageUpload = () => {
    setImages('');
    setPrevImage(null);
    setValue('logo', '', { shouldValidate: true });
  };

  //:: State management
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryError, setCategoryError] = useState(false);

  const handleNextStep = () => {
    setCurrentStep(2);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setCategoryError(false);
  };

  //:: HTTP POST
  const handleCreateBusiness = async (data) => {
    setIsSending(true);
    if (selectedCategory === null) {
      // Show error message if a category is not selected
      setCategoryError(true);
      return;
    }

    try {
      // We get sender_id as a string, but here we need it as a number
      const ownerId = Number(localStorage.getItem('sender_id'));
      const categoryText = categoryTexts[selectedCategory];

      const requestBody = {
        owner_id: ownerId,
        name: data.businessName,
        address: data.address,
        email: data.email,
        website: data.website,
        taxid: 'XXAAAAXXXCXXX3',
        description: data.businessDescription,
        context: 'Context is currently empty.',
        category: categoryText,
        logo: images || null, // Use the first image as logo
        createdAt: new Date().toISOString(),
        updatedAt: null,
      };

      const response = await addBusiness(requestBody);

      if (response) {
        localStorage.setItem('businessToast', 'true');
        onClose();
        navigate('/temp-route', { replace: true });
        setTimeout(() => navigate('/business', { replace: true }), 0);
      } else {
        console.error('Error when creating business');
      }
    } catch (error) {
      console.error('Error response data:', error.response?.data);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      {isOpen && (
        <div className="modal-background">
          <div className="modal-wrapper">
            <div className="modal-container">
              <div className="modal-header">
                <div className="modal-header__title-group">
                  <img
                    src={NewBusinessIcon}
                    alt="Business icon"
                    className="modal-icon"
                  />
                  <h2 className="modal-title">
                    {t('create_business.new_business_title')}
                  </h2>
                </div>
                <button
                  className="modal-close"
                  onClick={() => {
                    onClose();
                    resetImageUpload();
                  }}
                >
                  <img
                    src={CloseModalIcon}
                    alt="Close modal icon"
                    className="modal-close-icon"
                  />
                </button>
              </div>
              <p className="modal-description">
                {t('create_business.modal_description')}
              </p>

              {/* Progress Bars */}
              <div className="progress-indicators">
                <div
                  className={`progress-bar ${currentStep >= 1 ? 'active' : ''}`}
                ></div>
                <div
                  className={`progress-bar ${currentStep >= 2 ? 'active' : ''}`}
                ></div>
                <span>{currentStep}/2</span>
              </div>

              {currentStep === 1 ? (
                <>
                  {/* Form */}
                  <form method="POST" className="modal-form">
                    <div className="catalog-modal-form__field">
                      <div className="catalog-item-upload">
                        <div className="catalog-item-upload__main">
                          <div className="catalog-item-upload__image">
                            {/* <img
                              src={UploadIcon}
                              alt="Upload icon"
                              className="catalog-item-upload__icon"
                              onClick={() =>
                                document
                                  .getElementById('upload-item-images')
                                  .click()
                              }
                            /> */}
                            <img
                              src={prevImage || UploadIcon}
                              alt="Uploaded Preview"
                              className="catalog-item-upload__icon--bussines"
                              onClick={() =>
                                document
                                  .getElementById('upload-item-images')
                                  .click()
                              }
                            />
                          </div>
                          <div className="catalog-item-upload__text">
                            <p className="catalog-item-upload__title">
                              {t('create_business.form_fields.uploadTitle')}
                            </p>
                            <p className="catalog-item-upload__subtitle">
                              {t('create_business.form_fields.uploadSubtitle')}
                            </p>
                          </div>
                          <input
                            type="file"
                            id="upload-item-images"
                            accept=".jpg, .jpeg, .png"
                            style={{ display: 'none' }}
                            onChange={(event) => {
                              handleImageUpload(event);
                              setValue('images', images, {
                                shouldValidate: true,
                              });
                            }}
                          />
                          <button
                            type="button"
                            className="catalog-item-upload__button"
                            onClick={() =>
                              document
                                .getElementById('upload-item-images')
                                .click()
                            }
                          >
                            {t('create_business.buttons.upload')}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="modal-form__field">
                      <label
                        htmlFor="business-name"
                        className="modal-form__label"
                      >
                        {t('create_business.form_fields.business_name')}
                      </label>
                      <input
                        type="text"
                        className={`modal-form__input ${
                          errors.businessName ? 'error' : ''
                        }`}
                        placeholder={t(
                          'create_business.form_fields.business_name_placeholder'
                        )}
                        {...register('businessName', {
                          required: t(
                            'create_business.form_fields.business_name_required'
                          ),
                        })}
                      />
                      {errors.businessName && (
                        <span className="modal-form__error">
                          <img
                            src={InfoIcon}
                            alt="info icon"
                            className="error-icon"
                          />
                          {errors.businessName.message}
                        </span>
                      )}
                    </div>
                    <div className="modal-form__field">
                      <label
                        htmlFor="business-description"
                        className="modal-form__label"
                      >
                        {t('create_business.form_fields.business_description')}
                      </label>
                      <textarea
                        className={`modal-form__textarea ${
                          errors.businessDescription ? 'error' : ''
                        }`}
                        placeholder={t(
                          'create_business.form_fields.business_description_placeholder'
                        )}
                        {...register('businessDescription', {
                          required: t(
                            'create_business.form_fields.business_description_required'
                          ),
                        })}
                      />
                      {errors.businessDescription && (
                        <span className="modal-form__error">
                          <img
                            src={InfoIcon}
                            alt="info icon"
                            className="error-icon"
                          />
                          {errors.businessDescription.message}
                        </span>
                      )}
                    </div>
                    <div className="modal-form__field">
                      <label htmlFor="address" className="modal-form__label">
                        {t('create_business.form_fields.address')}
                      </label>
                      <input
                        type="text"
                        className={`modal-form__input ${
                          errors.address ? 'error' : ''
                        }`}
                        placeholder={t(
                          'create_business.form_fields.address_placeholder'
                        )}
                        {...register('address', {
                          required: t(
                            'create_business.form_fields.address_required'
                          ),
                        })}
                      />
                      {errors.address && (
                        <span className="modal-form__error">
                          <img
                            src={InfoIcon}
                            alt="info icon"
                            className="error-icon"
                          />
                          {errors.address.message}
                        </span>
                      )}
                    </div>

                    <p className="modal-form__title">
                      {t('create_business.form_fields.contact_information')}
                    </p>
                    <div className="modal-form__field">
                      <label htmlFor="email" className="modal-form__label">
                        {t('create_business.form_fields.email')}
                      </label>
                      <input
                        type="email"
                        className={`modal-form__input ${
                          errors.email ? 'error' : ''
                        }`}
                        placeholder={t(
                          'create_business.form_fields.email_placeholder'
                        )}
                        {...register('email', {
                          required: t(
                            'create_business.form_fields.email_required'
                          ),
                          pattern: {
                            value:
                              /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                            message: t(
                              'create_business.form_fields.email_invalid'
                            ),
                          },
                        })}
                      />
                      {errors.email && (
                        <span className="modal-form__error">
                          <img
                            src={InfoIcon}
                            alt="info icon"
                            className="error-icon"
                          />
                          {errors.email.message}
                        </span>
                      )}
                    </div>
                    <div className="modal-form__field">
                      <label htmlFor="website" className="modal-form__label">
                        {t('create_business.form_fields.website')}
                      </label>
                      <input
                        type="url"
                        className={`modal-form__input ${
                          errors.website ? 'error' : ''
                        }`}
                        placeholder={t(
                          'create_business.form_fields.website_placeholder'
                        )}
                        {...register('website', {
                          required: t(
                            'create_business.form_fields.website_required'
                          ),
                          pattern: {
                            value:
                              /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/,
                            message: 'The URL format is invalid',
                          },
                        })}
                      />
                      {errors.website && (
                        <span className="modal-form__error">
                          <img
                            src={InfoIcon}
                            alt="info icon"
                            className="error-icon"
                          />
                          {errors.website.message}
                        </span>
                      )}
                    </div>
                  </form>
                </>
              ) : (
                <>
                  {/* 2nd step: choose a category */}
                  <p className="modal-form__title">
                    {t('create_business.category_title')}
                  </p>
                  <div className="modal-categories">
                    {categoryTexts.map((text, index) => (
                      <button
                        key={index}
                        className={`modal-category-button ${
                          selectedCategory === index ? 'selected' : ''
                        }`}
                        onClick={() => handleCategorySelect(index)}
                      >
                        {text}
                      </button>
                    ))}
                  </div>
                  {categoryError && (
                    <span className="modal-form__error">
                      <img
                        src={InfoIcon}
                        alt="info icon"
                        className="error-icon"
                      />
                      {t('create_business.form_fields.category_required')}
                    </span>
                  )}
                </>
              )}
            </div>

            {/* Footer with Buttons */}
            <div className="modal-footer">
              {currentStep === 1 ? (
                <>
                  <button
                    className="button-border-green"
                    onClick={() => {
                      onClose();
                      resetImageUpload();
                    }}
                  >
                    {t('create_business.buttons.cancel')}
                  </button>
                  <button
                    className="button-fill-green"
                    onClick={handleSubmit(handleNextStep)}
                  >
                    {t('create_business.buttons.next')}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="button-border-green"
                    onClick={() => setCurrentStep(1)}
                  >
                    {t('create_business.buttons.back')}
                  </button>
                  <button
                    type="button"
                    disabled={isSending}
                    className="button-fill-green"
                    onClick={handleSubmit(handleCreateBusiness)}
                    style={{
                      background: isSending ? '#c9c9c9' : null,
                    }}
                  >
                    {t('create_business.buttons.create_business')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateBusinessModal;
